import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Briefcase, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PageBreadcrumb } from '@/components/seo/PageBreadcrumb';
import { getPublicSiteUrl } from '@/lib/seo';
import { renderSocialMetaTags } from '@/components/seo/SocialMetaTags';
import { getLocaleFromPathname, buildPathForLocale } from '@/lib/intl-seo';
import api from '@/lib/api';
import { toast } from 'sonner';

const CareersPage = () => {
  const { lang } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const site = getPublicSiteUrl();
  const langCode =
    lang || getLocaleFromPathname(location.pathname) || (i18n.language || 'en').substring(0, 2);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [area, setArea] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const breadcrumbItems = useMemo(
    () => [
      { label: t('common.home', 'Ana Sayfa'), to: buildPathForLocale(langCode, []) },
      { label: t('careers.title'), to: null },
    ],
    [t, langCode],
  );

  const canonicalPath = buildPathForLocale(langCode, ['careers']);
  const canonicalUrl =
    site && canonicalPath !== '/'
      ? `${site.replace(/\/$/, '')}${canonicalPath === '/' ? '' : canonicalPath}`
      : '';

  const pageTitle = `${t('careers.title')} | Turkish E-Market`;
  const metaDesc = t('careers.metaDescription');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) {
      toast.error(t('careers.requiredFields'));
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/careers/apply', {
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        area_of_interest: area.trim(),
        message: message.trim(),
        locale: langCode.substring(0, 2),
      });
      toast.success(t('careers.submitSuccess'));
      setFullName('');
      setEmail('');
      setPhone('');
      setArea('');
      setMessage('');
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        (Array.isArray(err.response?.data?.detail) && err.response.data.detail[0]?.msg) ||
        t('careers.submitError');
      toast.error(typeof detail === 'string' ? detail : t('careers.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="careers-page">
      <Helmet htmlAttributes={{ lang: langCode }}>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDesc} />
        {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
        {renderSocialMetaTags({
          title: pageTitle,
          description: metaDesc,
          url: canonicalUrl,
          image: site ? `${site.replace(/\/$/, '')}/logo.png` : '',
          locale: langCode,
        })}
      </Helmet>

      <div className="container mx-auto px-4 pt-6">
        <PageBreadcrumb items={breadcrumbItems} />
      </div>

      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="container mx-auto px-4 py-14 text-center">
          <Briefcase className="w-14 h-14 mx-auto mb-4 opacity-90" aria-hidden />
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{t('careers.heroTitle')}</h1>
          <p className="text-lg text-slate-200 max-w-2xl mx-auto">{t('careers.heroSubtitle')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-10">
        <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm border p-6 md:p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('careers.formTitle')}</h2>
          <p className="text-sm text-muted-foreground mb-6">{t('careers.formIntro')}</p>

          <form className="space-y-5" onSubmit={onSubmit}>
            <div className="space-y-2">
              <Label htmlFor="careers-name">{t('careers.fullName')} *</Label>
              <Input
                id="careers-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="name"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="careers-email">{t('careers.email')} *</Label>
              <Input
                id="careers-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="careers-phone">{t('careers.phone')}</Label>
              <Input
                id="careers-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="careers-area">{t('careers.area')}</Label>
              <Input
                id="careers-area"
                placeholder={t('careers.areaPlaceholder')}
                value={area}
                onChange={(e) => setArea(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="careers-message">{t('careers.message')}</Label>
              <Textarea
                id="careers-message"
                rows={5}
                placeholder={t('careers.messagePlaceholder')}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="resize-y min-h-[120px]"
              />
            </div>
            <p className="text-xs text-muted-foreground">{t('careers.privacyNote')}</p>
            <Button type="submit" className="w-full sm:w-auto" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  {t('careers.submitting')}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" aria-hidden />
                  {t('careers.submit')}
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CareersPage;
